import React, { memo, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimeRangeToggle from './TimeRangeToggle';
import { useTopPerformingCampaigns } from '../../hooks/useTopPerformingCampaigns';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';

const TopPerformingCampaigns: React.FC = () => {
  const [timeRange, setTimeRange] = useState<EngagementTimeRange>('90d');
  const { data, isLoading, error, refetch, isFetching } = useTopPerformingCampaigns(5, timeRange);

  const campaigns = useMemo(() => data ?? [], [data]);
  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
  const percentFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    []
  );

  return (
    <Card elevation={0} sx={{ height: '100%' }}>
      <CardHeader
        title={<Typography variant="h6">Top Performing Campaigns</Typography>}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <TimeRangeToggle value={timeRange} onChange={setTimeRange} disabled={isLoading} />
            <Tooltip title="Refresh campaigns">
              <span>
                <IconButton
                  onClick={() => refetch()}
                  disabled={isFetching}
                  aria-label="Refresh top performing campaigns"
                >
                  {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
      />
      <CardContent>
        {error ? (
          <Alert severity="error">Unable to load campaign performance.</Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Top campaign performance">
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell align="right">Delivered</TableCell>
                  <TableCell align="right">Open rate</TableCell>
                  <TableCell align="right">Click rate</TableCell>
                  <TableCell align="right">Engagement</TableCell>
                  <TableCell align="right">Delivery</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={600}>
                          {campaign.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sent {numberFormatter.format(campaign.sent)} on {new Date(campaign.createdAt).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{numberFormatter.format(campaign.delivered)}</TableCell>
                    <TableCell align="right">{percentFormatter.format(campaign.openRate / 100)}</TableCell>
                    <TableCell align="right">{percentFormatter.format(campaign.clickRate / 100)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${percentFormatter.format(campaign.engagementRate / 100)}`}
                        color="success"
                        size="small"
                        aria-label={`Engagement rate ${percentFormatter.format(campaign.engagementRate / 100)}`}
                      />
                    </TableCell>
                    <TableCell align="right">{percentFormatter.format(campaign.deliveryRate / 100)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default memo(TopPerformingCampaigns);
